import { useCallback, useEffect, useRef, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "./MediaLightbox.css";
import * as API from "@/helpers/api";
import FaceTagOverlay, { NEW_PERSON_PREFIX } from "./FaceTagOverlay";
import ObjectDetectionOverlay from "./ObjectDetectionOverlay";

const IMAGE_PLUGINS = [Fullscreen, Slideshow, Thumbnails, Zoom];
const MEDIA_PLUGINS = [Fullscreen, Slideshow, Thumbnails, Video, Zoom];
const THUMBNAIL_PRELOAD = 6;
const FILMSTRIP_THUMB_HEIGHT = 80;
export const IMAGE_INSIGHT_DELAY_MS = 4_000;
export const IMAGE_INSIGHT_REVEAL_INTERVAL_MS = 520;
const hideNavigationButton = () => null;

const isImageSlide = (slide) => slide && (!slide.type || slide.type === "image") && slide.src;

/** Prefer the lightweight strip preview; fall back to poster / full image src. */
export const getFilmstripThumbnailSource = (slide) => {
	if (!slide) return "";
	return [slide.thumbnail, slide.poster, isImageSlide(slide) ? slide.src : ""]
		.find(Boolean) || "";
};

/**
 * Fixed filmstrip height with width derived from the slide's aspect ratio.
 * When dimensions are unknown, width stays `auto` so the decoded image sets it.
 */
export const getFilmstripThumbnailSize = (slide, height = FILMSTRIP_THUMB_HEIGHT) => {
	const thumbHeight = Math.max(1, Number(height) || FILMSTRIP_THUMB_HEIGHT);
	const width = Number(slide?.width);
	const slideHeight = Number(slide?.height);
	if (width > 0 && slideHeight > 0) {
		return {
			width: Math.max(1, Math.round(thumbHeight * (width / slideHeight))),
			height: thumbHeight
		};
	}
	return { width: "auto", height: thumbHeight };
};

export const ProgressiveSlideContainer = ({ slide, children }) => {
	const previewSrc = getImageFallbackSource(slide);

	if (!previewSrc) return children;

	return (
		<div className="apit-media-progressive-slide">
			<img
				aria-hidden="true"
				alt=""
				className="apit-media-progressive-slide__preview"
				draggable="false"
				src={previewSrc}
			/>
			{children}
		</div>
	);
};

export const getImageFallbackSource = (slide) => {
	if (!isImageSlide(slide)) return "";
	return [slide.thumbnail, slide.fallbackSrc]
		.find((source) => source && source !== slide.src) || "";
};

export const getAdjacentImageSources = (slides, currentIndex) => (
	[-1, 1]
		.map((offset) => slides[currentIndex + offset])
		.filter(isImageSlide)
		.map((slide) => slide.src)
);

export const normalizeMediaSlides = (slides) => slides.map((slide) => {
	if (slide?.type !== "video") return slide;

	// The gallery has to use fallback dimensions while ffprobe is still pending,
	// but the Video plugin treats them as authoritative and constrains its player
	// to that box. Let the browser size videos from their decoded dimensions so
	// portrait/rotated clips can consume the carousel's full available height.
	const { width, height, ...videoSlide } = slide;
	return videoSlide;
});

export const orderDetectedObjects = (objects, hasRecognizedFaces = false) => (
	(Array.isArray(objects) ? objects : [])
		.filter((object) => {
			if (!object?.boundingBox || !object?.label) return false;
			return !(hasRecognizedFaces && String(object.label).toLowerCase() === "person");
		})
		.sort((left, right) => {
			const leftIsPerson = String(left.label).toLowerCase() === "person";
			const rightIsPerson = String(right.label).toLowerCase() === "person";
			if (leftIsPerson !== rightIsPerson) return leftIsPerson ? -1 : 1;
			return (Number(right.confidence) || 0) - (Number(left.confidence) || 0);
		})
);

/**
 * Shared full-screen viewer for clickable images and media across the app.
 *
 * Navigation is intentionally immediate and only the active slide is mounted.
 * This prevents the carousel's outgoing and incoming full-resolution images
 * from appearing side-by-side while a next/previous navigation is processing.
 */
export default function MediaLightbox({
	open,
	close,
	index = 0,
	slides = [],
	video = false,
	className,
	styles,
	zoom,
	thumbnails,
	on,
	enableFaceTagging = false,
	enableImageInsights = false,
	...props
}) {
	const hasMultipleSlides = slides.length > 1;
	const [currentIndex, setCurrentIndex] = useState(index);
	const [resolvedSlides, setResolvedSlides] = useState(() => normalizeMediaSlides(slides));
	const preloadedSourcesRef = useRef(new Set());
	const preloadImagesRef = useRef(new Map());

	// Manual face tagging (see FaceTagOverlay): boxes are only positioned
	// correctly at zoom === 1, so track zoom to hide them otherwise; the
	// known-people list is fetched once per lightbox session and cached.
	const [zoomLevel, setZoomLevel] = useState(1);
	const [personOptions, setPersonOptions] = useState(null);
	const [openFaceKey, setOpenFaceKey] = useState(null);
	const [savingFaceKey, setSavingFaceKey] = useState(null);
	const loadedFaceUploadIdsRef = useRef(new Set());
	const loadedAnalysisUploadIdsRef = useRef(new Set());
	const [insightDwellSlideId, setInsightDwellSlideId] = useState(null);
	const [insightReveal, setInsightReveal] = useState({ slideId: null, count: 0 });

	const ensurePersonOptions = useCallback((createdItem) => {
		if (createdItem) {
			setPersonOptions((current) => [...(current || []), createdItem]);
			return;
		}
		setPersonOptions((current) => {
			if (current) return current;
			API.get({ route: "face-analysis/persons" })
				.then((response) => {
					const persons = response?.data || response || [];
					setPersonOptions(persons.map((person) => ({ value: person._id, label: person.name || "Unknown" })));
				})
				.catch((error) => {
					console.error("Error loading people list:", error);
					setPersonOptions([]);
				});
			return current;
		});
	}, []);

	const handleAssignFace = useCallback(async (slide, face, value) => {
		const key = `${slide.id}:${face._id}`;
		setSavingFaceKey(key);
		try {
			const isNewPerson = value.startsWith(NEW_PERSON_PREFIX);
			const body = isNewPerson
				? { newPersonName: value.slice(NEW_PERSON_PREFIX.length) }
				: { personId: value };
			await API.post(`face-analysis/faces/${face._id}/reassign`, body);

			const refreshed = await API.get({ route: `face-analysis/uploads/${slide.id}/faces` });
			const updatedFaces = refreshed?.data || refreshed || [];
			setResolvedSlides((current) => current.map((s) => (s.id === slide.id ? { ...s, faces: updatedFaces } : s)));

			if (isNewPerson) setPersonOptions(null);
			window.dispatchEvent(new Event("apit-media-changed"));
		} catch (error) {
			console.error("Error reassigning face:", error);
		} finally {
			setSavingFaceKey(null);
			setOpenFaceKey(null);
		}
	}, []);

	const currentSlide = resolvedSlides[currentIndex];
	const currentSlideId = currentSlide?.id;
	const currentSlideIsImage = Boolean(isImageSlide(currentSlide));
	const currentFaces = Array.isArray(currentSlide?.faces) ? currentSlide.faces : [];
	const orderedObjects = orderDetectedObjects(currentSlide?.mediaAnalysis?.objects, currentFaces.length > 0);
	const genericPeopleCount = orderedObjects.filter((object) => String(object.label).toLowerCase() === "person").length;
	const namedPeopleCount = currentFaces.length;
	const peopleCount = namedPeopleCount + genericPeopleCount;
	const itemCount = Math.max(0, orderedObjects.length - genericPeopleCount);
	const insightTotal = namedPeopleCount + orderedObjects.length;
	const insightDataReady = currentSlide?.facesLoaded === true && currentSlide?.mediaAnalysisLoaded === true;
	const objectAnalysisComplete = ["completed", "partial"].includes(currentSlide?.mediaAnalysis?.status);
	const dwellComplete = insightDwellSlideId === currentSlideId;
	const revealedInsightCount = insightReveal.slideId === currentSlideId ? insightReveal.count : 0;
	const visibleFaceCount = Math.min(namedPeopleCount, revealedInsightCount);
	const visibleObjectCount = Math.max(0, revealedInsightCount - namedPeopleCount);
	const visiblePeopleCount = Math.min(peopleCount, revealedInsightCount);
	const visibleItemCount = Math.max(0, revealedInsightCount - peopleCount);
	let insightStatus = "";
	let insightStage = "loading";
	if (enableImageInsights && dwellComplete) {
		if (!insightDataReady) {
			insightStatus = "Loading image details…";
		} else if (insightTotal === 0) {
			insightStatus = objectAnalysisComplete
				? "No people or objects detected"
				: "Object analysis is pending";
		} else if (revealedInsightCount < peopleCount) {
			insightStage = "people";
			insightStatus = `People ${visiblePeopleCount}/${peopleCount}`;
		} else if (visibleItemCount < itemCount) {
			insightStage = "objects";
			insightStatus = `Objects ${visibleItemCount}/${itemCount}`;
		} else {
			insightStage = "complete";
			insightStatus = objectAnalysisComplete
				? `${peopleCount} ${peopleCount === 1 ? "person" : "people"} · ${itemCount} ${itemCount === 1 ? "object" : "objects"}`
				: `${peopleCount} ${peopleCount === 1 ? "person" : "people"} · Objects pending`;
		}
	}
	const faceIdForSlide = (key, slideId) => {
		if (!key) return null;
		const prefix = `${slideId}:`;
		return key.startsWith(prefix) ? key.slice(prefix.length) : null;
	};

	const render = {
		...(hasMultipleSlides ? {} : {
			buttonPrev: hideNavigationButton,
			buttonNext: hideNavigationButton
		}),
		// Keep the already-loaded gallery thumbnail visible at full size while
		// the original decodes. The lightbox's loading class controls the CSS
		// crossfade, so zoom and all native image behaviour remain untouched.
		slideContainer: ({ slide, children }) => (
			<div className="apit-media-slide-shell">
				<ProgressiveSlideContainer slide={slide}>{children}</ProgressiveSlideContainer>
				{(enableFaceTagging || enableImageInsights) && slide.id === currentSlideId && Array.isArray(slide.faces) && slide.faces.length > 0 && (
					<FaceTagOverlay
						slide={slide}
						zoom={zoomLevel}
						visibleCount={enableImageInsights ? visibleFaceCount : undefined}
						showLabels={enableImageInsights}
						animated={enableImageInsights}
						personOptions={personOptions}
						onRequestPersonOptions={ensurePersonOptions}
						openFaceId={faceIdForSlide(openFaceKey, slide.id)}
						savingFaceId={faceIdForSlide(savingFaceKey, slide.id)}
						onOpenFace={(faceId) => setOpenFaceKey(faceId ? `${slide.id}:${faceId}` : null)}
						onAssign={(face, value) => handleAssignFace(slide, face, value)}
					/>
				)}
				{enableImageInsights && slide.id === currentSlideId && zoomLevel <= 1.01 && orderedObjects.length > 0 && (
					<ObjectDetectionOverlay
						slide={slide}
						objects={orderedObjects}
						visibleCount={visibleObjectCount}
					/>
				)}
				{enableImageInsights && slide.id === currentSlideId && zoomLevel <= 1.01 && insightStatus && (
					<div className={`apit-media-insight-status apit-media-insight-status--${insightStage}`} role="status">
						<span className="apit-media-insight-status__dot" aria-hidden="true" />
						{insightStatus}
					</div>
				)}
			</div>
		),
		iconLoading: () => null,
		// Fixed-height filmstrip tiles sized to each slide's aspect ratio so
		// portrait/landscape thumbs are not forced into a shared crop box.
		thumbnail: ({ slide, rect }) => {
			const src = getFilmstripThumbnailSource(slide);
			if (!src) return undefined;
			const size = getFilmstripThumbnailSize(slide, rect?.height || FILMSTRIP_THUMB_HEIGHT);
			return (
				<img
					className="apit-media-filmstrip-thumb"
					src={src}
					alt=""
					draggable="false"
					width={typeof size.width === "number" ? size.width : undefined}
					height={size.height}
					style={{
						width: size.width,
						height: size.height,
						objectFit: "cover",
						display: "block"
					}}
				/>
			);
		},
		// The thumbnails plugin uses carousel.preload to decide how many items
		// appear in its filmstrip. Render lightweight placeholders for off-screen
		// image slides so a wider filmstrip does not download every original.
		slide: ({ offset }) => offset === 0 ? undefined : <span aria-hidden="true" />
	};

	// Lazily fetch detected faces for the photo currently on screen — only
	// once per uploadId per lightbox session, and only when face tagging is
	// actually enabled for this lightbox instance.
	useEffect(() => {
		if ((!enableFaceTagging && !enableImageInsights) || !open) return undefined;
		const slide = slides[currentIndex];
		if (!isImageSlide(slide) || !slide.id || loadedFaceUploadIdsRef.current.has(slide.id)) return undefined;
		loadedFaceUploadIdsRef.current.add(slide.id);

		let cancelled = false;
		API.get({ route: `face-analysis/uploads/${slide.id}/faces` })
			.then((response) => {
				if (cancelled) return;
				const faces = response?.data || response || [];
				setResolvedSlides((current) => current.map((s) => (s.id === slide.id ? { ...s, faces, facesLoaded: true } : s)));
			})
			.catch((error) => {
				console.error("Error loading faces for photo:", error);
				if (!cancelled) {
					setResolvedSlides((current) => current.map((s) => (
						s.id === slide.id ? { ...s, faces: [], facesLoaded: true } : s
					)));
				}
			});
		return () => { cancelled = true; };
	}, [enableFaceTagging, enableImageInsights, open, currentIndex, slides]);

	// Fetch general object detections in parallel with faces so both datasets
	// are ready by the time the four-second dwell completes.
	useEffect(() => {
		if (!enableImageInsights || !open) return undefined;
		const slide = slides[currentIndex];
		if (!isImageSlide(slide) || !slide.id || loadedAnalysisUploadIdsRef.current.has(slide.id)) return undefined;
		loadedAnalysisUploadIdsRef.current.add(slide.id);

		let cancelled = false;
		API.get({ route: `media-analysis/${slide.id}` })
			.then((response) => {
				if (cancelled) return;
				const mediaAnalysis = response?.data || response || null;
				setResolvedSlides((current) => current.map((candidate) => (
					candidate.id === slide.id ? { ...candidate, mediaAnalysis, mediaAnalysisLoaded: true } : candidate
				)));
			})
			.catch((error) => {
				if (error?.response?.status !== 404) console.error("Error loading image object analysis:", error);
				if (!cancelled) {
					setResolvedSlides((current) => current.map((candidate) => (
						candidate.id === slide.id ? { ...candidate, mediaAnalysis: null, mediaAnalysisLoaded: true } : candidate
					)));
				}
			});
		return () => { cancelled = true; };
	}, [enableImageInsights, open, currentIndex, slides]);

	// Dwell on each image for four seconds, then reveal one insight at a time.
	// Both pieces of state are keyed by upload id so an old slide can never leak
	// a completed timer or reveal count into a newly selected image.
	useEffect(() => {
		setInsightDwellSlideId(null);
		setInsightReveal({ slideId: currentSlideId || null, count: 0 });
		if (!enableImageInsights || !open || !currentSlideId || !currentSlideIsImage) return undefined;

		const timer = setTimeout(() => setInsightDwellSlideId(currentSlideId), IMAGE_INSIGHT_DELAY_MS);
		return () => clearTimeout(timer);
	}, [enableImageInsights, open, currentSlideId, currentSlideIsImage]);

	useEffect(() => {
		if (!enableImageInsights || !open || !dwellComplete || !insightDataReady || insightTotal <= 0) return undefined;
		if (revealedInsightCount >= insightTotal) return undefined;

		const timer = setTimeout(() => {
			setInsightReveal((current) => ({
				slideId: currentSlideId,
				count: Math.min(
					(current.slideId === currentSlideId ? current.count : 0) + 1,
					insightTotal
				)
			}));
		}, revealedInsightCount === 0 ? 0 : IMAGE_INSIGHT_REVEAL_INTERVAL_MS);
		return () => clearTimeout(timer);
	}, [
		currentSlideId,
		dwellComplete,
		enableImageInsights,
		insightDataReady,
		insightTotal,
		open,
		revealedInsightCount
	]);

	useEffect(() => {
		if (open) setCurrentIndex(index);
	}, [index, open]);

	useEffect(() => {
		setResolvedSlides(normalizeMediaSlides(slides));
	}, [slides]);

	const handleImageError = useCallback((slide, event) => {
		const fallbackSrc = getImageFallbackSource(slide);
		const image = event.currentTarget;
		if (!fallbackSrc || image.dataset.usedFallback === "true") return;

		// Replace the failed original immediately, then keep the corrected source
		// in React state so navigation and zoom do not retry the missing file.
		image.dataset.usedFallback = "true";
		image.removeAttribute("srcset");
		image.src = fallbackSrc;
		setResolvedSlides((current) => current.map((candidate) => (
			candidate === slide
				? { ...candidate, src: fallbackSrc, srcSet: undefined }
				: candidate
		)));
	}, []);

	useEffect(() => {
		if (!open) {
			preloadedSourcesRef.current.clear();
			preloadImagesRef.current.clear();
		}
	}, [open]);

	const preloadAdjacentImages = useCallback(() => {
		getAdjacentImageSources(resolvedSlides, currentIndex).forEach((src) => {
			if (preloadedSourcesRef.current.has(src)) return;
			preloadedSourcesRef.current.add(src);
			const image = new Image();
			image.decoding = "async";
			image.onload = image.onerror = () => preloadImagesRef.current.delete(src);
			preloadImagesRef.current.set(src, image);
			image.src = src;
		});
	}, [currentIndex, resolvedSlides]);

	// Keep carousel preload at zero so the active original has network priority.
	// Once that image has completed, warm only its immediate neighbours.
	useEffect(() => {
		if (!open || !isImageSlide(resolvedSlides[currentIndex])) return undefined;
		let frameId;
		let activeImage;
		const handleActiveImageLoaded = () => preloadAdjacentImages();

		frameId = requestAnimationFrame(() => {
			activeImage = document.querySelector(
				".apit-media-lightbox .yarl__slide_current .yarl__slide_image"
			);
			if (!activeImage) return;
			if (activeImage.complete && activeImage.naturalWidth > 0) handleActiveImageLoaded();
			else activeImage.addEventListener("load", handleActiveImageLoaded, { once: true });
		});

		return () => {
			cancelAnimationFrame(frameId);
			activeImage?.removeEventListener("load", handleActiveImageLoaded);
		};
	}, [currentIndex, open, preloadAdjacentImages, resolvedSlides]);

	return (
		<Lightbox
			className={["apit-media-lightbox", className].filter(Boolean).join(" ")}
			open={Boolean(open) && resolvedSlides.length > 0}
			close={close}
			index={Math.min(Math.max(index, 0), Math.max(resolvedSlides.length - 1, 0))}
			slides={resolvedSlides}
			plugins={video ? MEDIA_PLUGINS : IMAGE_PLUGINS}
			render={render}
			animation={{ swipe: 0, navigation: 0 }}
			carousel={{
				finite: true,
				preload: Math.min(THUMBNAIL_PRELOAD, Math.max(resolvedSlides.length - 1, 0)),
				spacing: 0,
				imageProps: (slide) => ({
					onError: (event) => handleImageError(slide, event)
				})
			}}
			styles={{
				container: {
					backgroundColor: "rgba(5, 8, 12, 0.76)",
					backdropFilter: "blur(18px)",
					WebkitBackdropFilter: "blur(18px)"
				},
				...styles
			}}
			zoom={{ maxZoomPixelRatio: 3, ...zoom }}
			thumbnails={{
				position: "bottom",
				hidden: false,
				showToggle: false,
				border: 0,
				padding: 0,
				gap: 8,
				height: FILMSTRIP_THUMB_HEIGHT,
				// Width is driven per-thumbnail via render.thumbnail + CSS; keep a
				// nominal default only for off-screen placeholders / track math.
				width: FILMSTRIP_THUMB_HEIGHT,
				imageFit: "cover",
				...thumbnails
			}}
			on={{
				...on,
				view: (event) => {
					setCurrentIndex(event.index);
					setZoomLevel(1);
					setOpenFaceKey(null);
					on?.view?.(event);
				},
				zoom: (event) => {
					setZoomLevel(event.zoom);
					on?.zoom?.(event);
				}
			}}
			video={video ? {
				controls: true,
				playsInline: true,
				autoPlay: true,
				...(typeof video === "object" ? video : {})
			} : undefined}
			{...props}
		/>
	);
}
